import React from "react";
import "./tables.css";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AppDialog from "./appDialog";

const valueCellStyle = {
  maxWidth: 220,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "0.85rem",
};

const headerCellStyle = {
  fontWeight: 600,
  fontSize: "0.78rem",
  color: "#4B5563",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  bgcolor: "#F8F9FA",
};

// Single, page-level sync dialog — render exactly ONE of these per page,
// not one per row/cell. It's entirely driven by the shared `matchCell`
// ref (set by DiffIndicatorIcon's onClick via handleMatchClickOpen), so
// clicking any row's Diff icon shows that row's data here. Keeping this
// to a single instance avoids mounting a stack of duplicate <Modal>
// overlays, which is unreliable for click handling in a real browser.
//
// Direction toggle:
//   Sync from {env} — bring that env's values into this (current) record.
//   Sync to {env}   — overwrite that env's record with this record's values.
// Either direction supports updating every differing field or just the
// ones checked.
const SyncDialog = ({
  open,
  matchCell,
  matchSelectedFields,
  setMatchSelectedFields,
  matchDirection,
  setMatchDirection,
  handleMatchClose,
  handleMatchConfirm,
}) => {
  let env = "";
  let currentRow = {};
  let currentTargetRow = {};
  let currentDiffFields = [];

  if (matchCell.current != null) {
    env = matchCell.current.cell.column.id;
    currentRow = matchCell.current.cell.row.original || {};
    currentTargetRow = matchCell.current.targetRow || {};
    currentDiffFields = matchCell.current.diffFields || [];
  }

  const isPush = matchDirection === "push";
  const hasDiff = currentDiffFields.length > 0;

  const toggleField = (field) => {
    if (matchSelectedFields.includes(field)) {
      setMatchSelectedFields(matchSelectedFields.filter((f) => f !== field));
    } else {
      setMatchSelectedFields([...matchSelectedFields, field]);
    }
  };

  const toggleButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.85rem",
    px: 2,
    "&.Mui-selected": {
      bgcolor: "#FCEEE1",
      color: "#C4620A",
      "&:hover": { bgcolor: "#FCEEE1" },
    },
  };

  return (
    <AppDialog
      open={open}
      onClose={handleMatchClose}
      title={`Sync with ${env}`}
      maxWidth="md"
      contentSx={{ maxHeight: "70vh" }}
      actions={
        <>
          <Button variant="text" color="inherit" size="small" onClick={handleMatchClose}>
            {hasDiff ? "Cancel" : "Close"}
          </Button>
          {hasDiff && (
            <>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                disabled={matchSelectedFields.length === 0}
                onClick={() => handleMatchConfirm(matchSelectedFields, matchDirection)}
              >
                Sync Selected
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                onClick={() => handleMatchConfirm(currentDiffFields, matchDirection)}
              >
                Sync All
              </Button>
            </>
          )}
        </>
      }
    >
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
        {`Compare this record against ${env} and choose what to update.`}
      </Typography>

      {!hasDiff ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#F0FBF3",
              borderRadius: "8px",
              p: 1.5,
              mb: 1,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: "#2BB041" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#1F2937" }}>
              {`No differences found — nothing to sync.`}
            </Typography>
          </Box>
        ) : (
          <>
            <ToggleButtonGroup
              value={matchDirection}
              exclusive
              size="small"
              onChange={(_event, value) => value && setMatchDirection(value)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="pull" sx={toggleButtonSx}>
                <CallReceivedIcon fontSize="small" sx={{ mr: 0.75 }} />
                {`Sync from ${env}`}
              </ToggleButton>
              <ToggleButton value="push" sx={toggleButtonSx}>
                <CallMadeIcon fontSize="small" sx={{ mr: 0.75 }} />
                {`Sync to ${env}`}
              </ToggleButton>
            </ToggleButtonGroup>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                bgcolor: "#F8F9FA",
                borderRadius: "8px",
                p: 1.5,
                mb: 2,
              }}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ color: "#9CA3AF", mt: "2px" }} />
              <Typography variant="body2" sx={{ color: "#4B5563" }}>
                {isPush
                  ? `Choose which fields to send to ${env}. "Sync Selected" updates just the checked fields there; "Sync All" updates every field that's different.`
                  : `Choose which fields to bring in from ${env}. "Sync Selected" updates just the checked fields here; "Sync All" updates every field that's different.`}
              </Typography>
            </Box>

            <TableContainer sx={{ maxHeight: "42vh", border: "1px solid #EEE", borderRadius: "8px" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={headerCellStyle} />
                    <TableCell sx={headerCellStyle}>Field</TableCell>
                    <TableCell sx={headerCellStyle}>Current value</TableCell>
                    <TableCell sx={headerCellStyle}>{`${env} value`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentDiffFields.map((field) => {
                    const currentValue = currentRow[field];
                    const targetValue = currentTargetRow[field];

                    return (
                      <TableRow key={field} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={matchSelectedFields.includes(field)}
                            onChange={() => toggleField(field)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{field}</TableCell>
                        <TableCell sx={valueCellStyle} title={String(currentValue ?? "")}>
                          {String(currentValue ?? "")}
                        </TableCell>
                        <TableCell sx={valueCellStyle} title={String(targetValue ?? "")}>
                          {String(targetValue ?? "")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
    </AppDialog>
  );
};

export default SyncDialog;
